import React, { FC, useMemo, useRef } from 'react';
import type { GroupBase } from 'react-select';
import Select from 'react-select/base';

import { FieldArray, FormikProps } from 'formik';

import BasicButton from '../../../../../components/buttons/basicButton';
//components
import { SearchableSelectType } from '../../../../../components/inputs/searchableSelect';
import styled from 'styled-components';

//constants
import aampeTheme from '../../../../../constants/aampeTheme';
import ICONS from '../../../../../constants/icons';

//hooks
import useCMSPlaceholderQuery from '../../../../../hooks/cms-placeholders/useCMSPlaceholderQuery';

// models
import {
    CartFilterFormValues,
    CMSFilterCreateModel,
} from '../../../../../models/cms-placeholder.model';

import CMSPlaceholderFilterRow from './CMSPlaceholderFilterRow';

interface CMSPlaceholderFilterModalProps {
    formikProps: FormikProps<CartFilterFormValues>;
    isDisabled: boolean;
    handleClose: () => void;
}
const CMSPlaceholderFilterModal: FC<CMSPlaceholderFilterModalProps> = ({
    formikProps,
    isDisabled,
    handleClose,
}) => {
    const placeholderRef =
        useRef<
            Select<SearchableSelectType, false, GroupBase<SearchableSelectType>>
        >(null);

    const { data: placeholderList } = useCMSPlaceholderQuery();

    const isValid = useMemo<boolean>(() => {
        return (formikProps.values.cms_filters ?? []).every(
            (filter) =>
                filter.cms_placeholder_id && filter.operator && filter.value,
        );
    }, [formikProps]);

    return (
        <FormContainer>
            <Container>
                <Heading>Only items that match specific filters</Heading>
                <FieldArray
                    name={'cms_filters'}
                    render={(arrayHelpers) => (
                        <Items>
                            {formikProps.values.cms_filters?.map(
                                (item: CMSFilterCreateModel, index: number) => (
                                    <CMSPlaceholderFilterRow
                                        key={item.cms_placeholder_id + index}
                                        index={index}
                                        placeholderList={
                                            placeholderList?.filterable || []
                                        }
                                        formikProps={formikProps}
                                        placeholderRef={placeholderRef}
                                        inputRowData={item}
                                        arrayHelpers={arrayHelpers}
                                        isDisabled={isDisabled}
                                    />
                                ),
                            )}
                            {!isDisabled && (
                                <TextContainer
                                    isValid={isValid}
                                    onClick={() => {
                                        if (isValid) {
                                            arrayHelpers.push({
                                                cms_placeholder_id: '',
                                                operator: '',
                                                value: '',
                                            });
                                            setTimeout(() =>
                                                placeholderRef.current?.focus(),
                                            );
                                        }
                                    }}
                                >
                                    <ICONS.plus
                                        size={18}
                                        color={
                                            isValid
                                                ? aampeTheme.colors.solidA02
                                                : aampeTheme.colors
                                                      .textAccessibleWG01
                                        }
                                    />
                                    <Text> add new filter</Text>
                                </TextContainer>
                            )}
                        </Items>
                    )}
                />
                <ActionContainer>
                    <StyledButton
                        isPrimary={false}
                        name="Back"
                        action={handleClose}
                    />
                    <StyledButton
                        isDisabled={isDisabled}
                        type="submit"
                        isPrimary
                        name="Save"
                    />
                </ActionContainer>
            </Container>
        </FormContainer>
    );
};

const Container = styled.div({
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: aampeTheme.spacing4(6),
});

const FormContainer = styled.div({
    display: 'flex',
    flexDirection: 'column',
});

const Items = styled.div({
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    gap: aampeTheme.spacing4(4),
});

const TextContainer = styled.div<{ isValid: boolean }>(({ isValid }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: aampeTheme.spacing4(1),
    cursor: isValid ? 'pointer' : 'default',
    color: isValid
        ? aampeTheme.colors.solidA02
        : aampeTheme.colors.textAccessibleWG01,
}));
const Text = styled.div({
    fontSize: aampeTheme.size.font.default,
    lineHeight: '20px',
    fontWeight: 400,
});

const Heading = styled.div({
    fontSize: aampeTheme.size.font.default,
    lineHeight: '20px',
    fontWeight: 500,
});

const StyledButton = styled(BasicButton)({
    borderRadius: aampeTheme.borderRadius.default,
    fontWeight: 500,
    fontSize: aampeTheme.size.font.default,
    lineHeight: '18px',
    padding: aampeTheme.spacing4(3, 11),
});

const ActionContainer = styled.div({
    display: 'flex',
    justifyContent: 'end',
    gap: aampeTheme.spacing4(4),
});
export default CMSPlaceholderFilterModal;
